// 수정 동굴 호수·폭포 연출 — 폭포 밑 물안개 · 호수 위에 떠서 도는 결정 조각 · 수면에 깔린 빛 안개. 각각 드로콜 1.
// 자료는 cave_crystal_cave.json 의 falls [[x,y0,z,y1]…] · lake [cx, 수면 y, cz, rx, rz](place_cave_crystals.py 가 쓴다).
// CaveCrystalFx 가 «동굴 안 정도» amt(ref, 0~1)를 넘겨 준다 — 0 이면 셋 다 안 그린다.
import { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { CLOUD_TIME } from './sky/skyLight';

const REDUCED = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const MIST_N = 220;
const MOTE_N = 30;

function makeRng(seed) {
  let h = seed >>> 0;
  return () => { h = (Math.imul(h, 1664525) + 1013904223) >>> 0; return h / 4294967296; };
}

const MIST_VERT = /* glsl */`
attribute vec4 aSeed;
uniform float uTime;
varying float vA;
void main() {
  float t = fract(uTime * (0.08 + 0.06 * aSeed.y) + aSeed.x);
  float a = aSeed.z * 6.2832;
  float r = 0.3 + 2.6 * sqrt(t) * (0.5 + 0.5 * aSeed.w);
  vec3 p = position + vec3(cos(a) * r, 0.2 + 3.2 * t * (0.6 + 0.4 * aSeed.y), sin(a) * r);
  vec4 mv = viewMatrix * vec4(p, 1.0);
  vA = smoothstep(0.0, 0.15, t) * (1.0 - t) * smoothstep(1.0, 3.0, -mv.z);
  gl_PointSize = min((18.0 + 40.0 * t) * (12.0 / max(-mv.z, 1.0)), 90.0);
  gl_Position = projectionMatrix * mv;
}`;
const MIST_FRAG = /* glsl */`
uniform float uAmt;
varying float vA;
void main() {
  float r = 1.0 - smoothstep(0.0, 0.5, length(gl_PointCoord - 0.5));
  gl_FragColor = vec4(vec3(0.62, 0.78, 1.0) * r * r * vA * 0.16 * uAmt, 1.0);
}`;

/** 폭포 밑 물안개 — 폭포 기둥 밑동에서 솟아 퍼지며 사라지는 큰 부드러운 점(가산). */
function FallMist({ falls, amt }) {
  const ref = useRef();
  const geo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const pos = new Float32Array(MIST_N * 3);
    const seed = new Float32Array(MIST_N * 4);
    const rnd = makeRng(7919);
    for (let i = 0; i < MIST_N; i += 1) {
      const f = falls[i % falls.length];
      pos.set([f[0], f[1], f[2]], i * 3);
      seed.set([rnd(), rnd(), rnd(), rnd()], i * 4);
    }
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('aSeed', new THREE.BufferAttribute(seed, 4));
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e7);
    return g;
  }, [falls]);
  const mat = useMemo(() => new THREE.ShaderMaterial({
    vertexShader: MIST_VERT, fragmentShader: MIST_FRAG, transparent: true, depthWrite: false,
    blending: THREE.AdditiveBlending, uniforms: { uTime: { value: 0 }, uAmt: { value: 0 } },
  }), []);
  useEffect(() => () => { geo.dispose(); mat.dispose(); }, [geo, mat]);
  useFrame(() => {
    const m = ref.current;
    if (!m) return;
    m.visible = amt.current > 0.01;
    m.material.uniforms.uAmt.value = amt.current;
    m.material.uniforms.uTime.value = REDUCED ? 0 : CLOUD_TIME.value;
  });
  return <points ref={ref} geometry={geo} material={mat} frustumCulled={false} renderOrder={5} name="cave-fall-mist" />;
}

const MOTE_COLORS = ['#b98cff', '#8a54e8', '#d6c0ff', '#6fd8ff', '#ffa94d', '#c66ee6'];

/** 호수 위 부유 결정 — 수면 위 1~6m 에 떠서 천천히 돌고 오르내리는 길쭉한 팔면체 조각. */
function LakeMotes({ lake, amt }) {
  const ref = useRef();
  const motes = useMemo(() => {
    const [cx, y, cz, rx, rz] = lake;
    const rnd = makeRng(104729);
    return Array.from({ length: MOTE_N }, () => {
      const a = rnd() * Math.PI * 2, r = Math.sqrt(rnd()) * 0.8;
      const big = rnd() < 0.15;
      return {
        x: cx + Math.cos(a) * rx * r, z: cz + Math.sin(a) * rz * r, y: y + 1.0 + rnd() * 5.0,
        s: (big ? 0.55 : 0.18 + rnd() * 0.22), el: 1.6 + rnd() * 1.8, ph: rnd() * 6.28, sp: 0.15 + rnd() * 0.35,
        tilt: (rnd() - 0.5) * 0.9, c: MOTE_COLORS[Math.floor(rnd() * rnd() * MOTE_COLORS.length)],
      };
    });
  }, [lake]);
  // 면마다 밝기를 구워 넣는다(정점색) — 조명 없이도 깎은 면이 읽힌다. 위쪽 면이 밝고 아래가 어둡다.
  const geo = useMemo(() => {
    const g = new THREE.OctahedronGeometry(1, 0).toNonIndexed();
    const pos = g.attributes.position;
    const col = new Float32Array(pos.count * 3);
    for (let f = 0; f < pos.count; f += 3) {
      const cy = (pos.getY(f) + pos.getY(f + 1) + pos.getY(f + 2)) / 3;
      const cx = (pos.getX(f) + pos.getX(f + 1) + pos.getX(f + 2)) / 3;
      const k = 0.45 + 0.45 * (cy * 1.5 + 0.5) + 0.12 * cx;
      for (let v = 0; v < 3; v += 1) col.set([k, k, k], (f + v) * 3);
    }
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    return g;
  }, []);
  const mat = useMemo(() => new THREE.MeshBasicMaterial({ color: 0xffffff, vertexColors: true, transparent: true, opacity: 0.9, depthWrite: true }), []);
  useEffect(() => {
    const m = ref.current;
    if (m) { const c = new THREE.Color(); motes.forEach((o, i) => m.setColorAt(i, c.set(o.c).multiplyScalar(1.4))); m.instanceColor.needsUpdate = true; }
    return () => { geo.dispose(); mat.dispose(); };
  }, [motes, geo, mat]);
  const o3 = useMemo(() => new THREE.Object3D(), []);
  useFrame(() => {
    const m = ref.current;
    if (!m) return;
    m.visible = amt.current > 0.01;
    if (!m.visible) return;
    const t = REDUCED ? 0 : CLOUD_TIME.value;
    motes.forEach((o, i) => {
      o3.position.set(o.x, o.y + Math.sin(t * o.sp + o.ph) * 0.45, o.z);
      o3.rotation.set(o.tilt, t * o.sp * 0.8 + o.ph, o.tilt * 0.5);
      o3.scale.set(o.s, o.s * o.el, o.s);
      o3.updateMatrix();
      m.setMatrixAt(i, o3.matrix);
    });
    m.instanceMatrix.needsUpdate = true;
    m.material.opacity = 0.9 * amt.current;
  });
  return <instancedMesh ref={ref} args={[geo, mat, MOTE_N]} frustumCulled={false} name="cave-lake-motes" />;
}

const FOG_VERT = /* glsl */`
varying vec3 vW;
varying vec2 vUv;
void main() {
  vUv = uv;
  vec4 w = modelMatrix * vec4(position, 1.0);
  vW = w.xyz;
  gl_Position = projectionMatrix * viewMatrix * w;
}`;
const FOG_FRAG = /* glsl */`
uniform float uAmt, uTime;
varying vec3 vW;
varying vec2 vUv;
float h21(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float vn(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(h21(i), h21(i + vec2(1.0, 0.0)), f.x), mix(h21(i + vec2(0.0, 1.0)), h21(i + vec2(1.0)), f.x), f.y); }
void main() {
  vec2 q = vW.xz * 0.12;
  float n = vn(q + vec2(uTime * 0.05, uTime * 0.03)) * 0.6 + vn(q * 2.3 - vec2(uTime * 0.07, 0.0)) * 0.4;
  float e = length((vUv - 0.5) * 2.0);
  float a = smoothstep(0.35, 0.8, n) * (1.0 - smoothstep(0.55, 1.0, e));
  vec3 c = mix(vec3(0.55, 0.42, 1.0), vec3(0.35, 0.85, 1.0), vn(q * 0.5 + 7.0));
  gl_FragColor = vec4(c * a * 0.11 * uAmt, 1.0);
}`;

/** 수면 빛 안개 — 수면 0.4m 위에 깔린 타원 판 하나(가산·깊이 안 씀), 느리게 흐르는 두 옥타브 노이즈. */
function LakeFog({ lake, amt }) {
  const ref = useRef();
  const [cx, y, cz, rx, rz] = lake;
  const geo = useMemo(() => new THREE.PlaneGeometry(rx * 2.4, rz * 2.4).rotateX(-Math.PI / 2), [rx, rz]);
  const mat = useMemo(() => new THREE.ShaderMaterial({
    vertexShader: FOG_VERT, fragmentShader: FOG_FRAG, transparent: true, depthWrite: false,
    blending: THREE.AdditiveBlending, side: THREE.DoubleSide, uniforms: { uTime: { value: 0 }, uAmt: { value: 0 } },
  }), []);
  useEffect(() => () => { geo.dispose(); mat.dispose(); }, [geo, mat]);
  useFrame(() => {
    const m = ref.current;
    if (!m) return;
    m.visible = amt.current > 0.01;
    m.material.uniforms.uAmt.value = amt.current;
    m.material.uniforms.uTime.value = REDUCED ? 0 : CLOUD_TIME.value;
  });
  return <mesh ref={ref} geometry={geo} material={mat} position={[cx, y + 0.4, cz]} renderOrder={4} name="cave-lake-fog" />;
}

export default function CaveWaterFx({ falls, lake, amt }) {
  return (
    <>
      {falls?.length > 0 && <FallMist falls={falls} amt={amt} />}
      {lake && <LakeMotes lake={lake} amt={amt} />}
      {lake && <LakeFog lake={lake} amt={amt} />}
    </>
  );
}
